/**
 * custom-buy-button.js — v2.5.1 (2026-08-27, CK/Limitless)
 *
 * WHAT IT DOES
 *   (1) SUBMIT: Add to Cart for the custom buy box (POST /cart/add.js) —
 *       replaces the v1 proxy-click that depended on a broken inline script.
 *   (4) COLOR-AWARE SELECTION (new in 2.3): bundle cards resolve to the
 *       variant matching the machine color currently selected in the theme's
 *       variant picker (via /products/{handle}.js). Changing color after
 *       selecting a bundle re-resolves it. Falls back to the card's default
 *       variant if the handle/color can't be resolved.
 *   (3) DECORATION (new in 2.2): injects "what's included" sublines and
 *       BEST VALUE / BEST SELLER flags on the cards (config map below),
 *       hides the theme's flickering legacy Best Value tab, and opens the
 *       card info (i) modal on all devices via delegation.
 *   (2) SELECTION (new in 2.1): makes the machine/bundle carousel cards work.
 *       Clicking a card SWAPS the buy-box selection to that card's variant
 *       (a bundle replaces the machine — it contains it), updates the active
 *       state and the button price label. Cards were previously wired by a
 *       custom element (`addon-single-product`) that never binds on templates
 *       using other wrapper tags (e.g. `addon-single-product-standalone`).
 *
 * DEPLOYS TO   Shopify theme asset: assets/custom-buy-button.js (replace contents)
 * DEPENDS ON   Nothing (vanilla JS). Coexists with addons-kit.js (true add-ons).
 * KILL SWITCH  Restore previous version from the code editor's "Older versions".
 *
 * GOTCHAS
 *   - Root inputs: #root_input (sticky form, uses data-seek) and
 *     #root_input_button (visible form, uses the historical `data-seeb` typo).
 *     The price total sums document [data-seek]; keeping the visible root on
 *     `data-seeb` is INTENTIONAL so the root is only counted once. Do not
 *     "fix" the typo here without rescoping the price math.
 *   - A card with no data-value is the "base product" card: selecting it
 *     restores the roots captured at load time.
 *   - We stopImmediatePropagation on card clicks so any legacy handler that
 *     happens to be alive can't double-handle with append semantics.
 */
(function () {
  'use strict';

  var CK_NS = 'ck-cbb';

  /* ================================================================== *
   * Shared helpers
   * ================================================================== */

  function ckAllRootInputs() {
    return {
      sticky: document.querySelectorAll('form#bulk-add-to-cart #root_input, form#bulk-add-to-cart input[name="id"][data-seek]'),
      visible: document.querySelectorAll('form.bulk-add-to-cart_button #root_input_button, form.bulk-add-to-cart_button input[name="id"][data-seeb]')
    };
  }

  // Selected base price (cents) lives in JS state so the label never depends
  // on which form variant/attribute the template happened to render.
  var ckSelectedSeek = null;

  function ckRecalcPriceLabel() {
    var total = 0;

    if (ckSelectedSeek !== null && !isNaN(Number(ckSelectedSeek))) {
      total += Number(ckSelectedSeek);
    }

    // Add true add-on inputs (kit/warranty widgets) but never the root inputs
    // (the base selection is already counted from state above).
    var seekInputs = document.querySelectorAll('input[data-seek]');
    Array.prototype.forEach.call(seekInputs, function (el) {
      if (el.id === 'root_input' || el.id === 'root_input_button') return;
      var v = Number(el.getAttribute('data-seek'));
      if (!isNaN(v)) total += v;
    });

    // Fallback: no state captured at all (shouldn't happen) -> old behavior.
    if (ckSelectedSeek === null && total === 0) {
      Array.prototype.forEach.call(seekInputs, function (el) {
        var v = Number(el.getAttribute('data-seek'));
        if (!isNaN(v)) total += v;
      });
    }
    var formatted = (total / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    var totalBuy = document.getElementById('total_buy');
    if (totalBuy) totalBuy.innerHTML = formatted;
    var labelSpans = document.querySelectorAll('.total_buy_button');
    Array.prototype.forEach.call(labelSpans, function (span) {
      span.innerHTML = formatted;
    });
  }

  /* ================================================================== *
   * (0) COLOR ENGINE — resolve variants by the machine color in play
   * ================================================================== */

  var ckProductCache = {};

  function ckFetchProduct(handle) {
    if (!handle) return Promise.reject(new Error('no handle'));
    if (ckProductCache[handle]) return ckProductCache[handle];
    ckProductCache[handle] = fetch('/products/' + handle + '.js', {
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('product fetch ' + r.status);
      return r.json();
    });
    return ckProductCache[handle];
  }

  function ckMachineHandle() {
    var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  // The theme's own product form is the source of truth for the machine
  // variant currently selected by the swatches.
  function ckThemeFormVariantId() {
    var el = document.querySelector(
      'form[id^="product-form-"] [name="id"], form[action*="/cart/add"] [name="id"]'
    );
    var v = el ? parseInt(el.value, 10) : NaN;
    return isNaN(v) ? null : v;
  }

  function ckColorOptionIndex(product) {
    if (!product || !product.options) return 0;
    for (var i = 0; i < product.options.length; i++) {
      var name = typeof product.options[i] === 'string'
        ? product.options[i]
        : (product.options[i] && product.options[i].name) || '';
      if (/colou?r/i.test(name)) return i;
    }
    return 0;
  }

  // Current machine color string (e.g. "Mulberry"), or null.
  function ckCurrentColor() {
    var handle = ckMachineHandle();
    var vid = ckThemeFormVariantId();
    if (!handle || !vid) return Promise.resolve(null);
    return ckFetchProduct(handle).then(function (product) {
      var idx = ckColorOptionIndex(product);
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].id === vid) {
          return product.variants[i].options[idx] || null;
        }
      }
      return null;
    }).catch(function () { return null; });
  }

  // Resolve a bundle handle + color to { id, price } (price in cents).
  function ckResolveByColor(handle, color) {
    return ckFetchProduct(handle).then(function (product) {
      var idx = ckColorOptionIndex(product);
      var fallback = null;
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        if (v.available !== false && !fallback) fallback = v;
        if (color && (v.options[idx] || '').toLowerCase() === color.toLowerCase()) {
          return { id: v.id, price: v.price };
        }
      }
      fallback = fallback || product.variants[0];
      return fallback ? { id: fallback.id, price: fallback.price } : null;
    });
  }

  // The active card, if it points at a separate product (a bundle).
  function ckActiveBundleCard() {
    var card = document.querySelector('[data-addons-product].active[data-product-handle]');
    return card || null;
  }

  // Re-resolve current state (active bundle or bare machine) to the live color.
  function ckSyncSelectionToColor() {
    var card = ckActiveBundleCard();
    if (card) {
      var handle = card.getAttribute('data-product-handle');
      ckCurrentColor().then(function (color) {
        return ckResolveByColor(handle, color);
      }).then(function (v) {
        if (v && v.id) ckApplySelection(String(v.id), String(v.price));
      }).catch(function (err) {
        console.warn('[' + CK_NS + '] color resolve failed, keeping default variant', err);
      });
      return;
    }
    // Bare machine selected: mirror the theme form's variant into our roots.
    var vid = ckThemeFormVariantId();
    var mHandle = ckMachineHandle();
    if (!vid || !mHandle) return;
    var machineCardActive = document.querySelector('[data-addons-product].active');
    // Only sync when no bundle card is active (machine card or nothing).
    if (machineCardActive && machineCardActive.hasAttribute('data-product-handle')) return;
    ckFetchProduct(mHandle).then(function (product) {
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].id === vid) {
          ckApplySelection(String(vid), String(product.variants[i].price));
          ckBaseSelection = { id: String(vid), seek: String(product.variants[i].price) };
          return;
        }
      }
    }).catch(function () {});
  }

  // Any change in the theme's variant picker re-syncs color.
  document.addEventListener('change', function (e) {
    var inForm = e.target && e.target.closest &&
      e.target.closest('form[id^="product-form-"], variant-picker, .variant-picker, [data-variant-picker]');
    if (!inForm) return;
    // let the theme update its hidden [name=id] first
    setTimeout(ckSyncSelectionToColor, 50);
  }, true);

  /* ================================================================== *
   * (1) SELECTION LAYER — carousel cards (machine vs. bundles)
   * ================================================================== */

  // Capture the page's base selection once, so the base card can restore it.
  var ckBaseSelection = null;
  function ckCaptureBaseSelection() {
    if (ckBaseSelection) return;
    var roots = ckAllRootInputs();
    var src = roots.sticky[0] || roots.visible[0];
    if (!src) return;
    ckBaseSelection = {
      id: src.value,
      seek: src.getAttribute('data-seek') || src.getAttribute('data-seeb') || '0'
    };
    if (ckSelectedSeek === null) ckSelectedSeek = ckBaseSelection.seek;
  }

  function ckApplySelection(variantId, seek) {
    ckSelectedSeek = seek;
    var roots = ckAllRootInputs();
    Array.prototype.forEach.call(roots.sticky, function (input) {
      input.value = variantId;
      input.setAttribute('data-seek', seek);
    });
    Array.prototype.forEach.call(roots.visible, function (input) {
      input.value = variantId;
      // visible root intentionally stays on data-seeb (see GOTCHAS)
      input.setAttribute('data-seeb', seek);
    });
    ckRecalcPriceLabel();
  }

  function ckHandleCardClick(event) {
    var card = event.target.closest('[data-addons-product]');
    if (!card) return;

    // Tooltip (i) icons live inside cards; let them behave normally.
    if (event.target.closest('[data-button-handle-tooltip], [data-modal-dismiss]')) return;

    event.preventDefault();
    // Preempt any legacy handler so we never double-handle a card click.
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    var available = card.getAttribute('data-available');
    if (available !== null && available === 'false') return;
    if (card.classList.contains('active')) return;

    ckCaptureBaseSelection();

    var variantId = card.getAttribute('data-value');
    var seek = card.getAttribute('data-seek') || '0';

    if (!variantId && ckBaseSelection) {
      variantId = ckBaseSelection.id;
      seek = ckBaseSelection.seek;
    }
    if (!variantId) {
      console.error('[' + CK_NS + '] Card has no data-value and no base selection captured.');
      return;
    }

    // Active state within this card group only.
    var group = card.getAttribute('data-for');
    var siblings = group
      ? document.querySelectorAll('[data-addons-product][data-for="' + group + '"]')
      : card.parentElement.querySelectorAll('[data-addons-product]');
    Array.prototype.forEach.call(siblings, function (el) {
      el.classList.remove('active');
    });
    card.classList.add('active');

    ckApplySelection(variantId, seek);
    ckSyncExpandPanel();

    // Bundle card: upgrade the instant default to the color-correct variant.
    if (card.hasAttribute('data-product-handle')) {
      ckSyncSelectionToColor();
    }

    // Optional gummy-mix reveal section — guarded (was a null crash upstream).
    var mixes = document.querySelector('#mixes-addons');
    if (mixes) {
      if (group === 'gummy-mixer-addon' && card.getAttribute('data-seek')) {
        mixes.classList.add('is-open');
      } else {
        mixes.classList.remove('is-open');
      }
    }
  }

  // Capture-phase delegation: survives re-renders, beats legacy bubbling handlers.
  document.addEventListener('click', ckHandleCardClick, true);

  /* ================================================================== *
   * (2) SUBMIT LAYER — the actual add to cart
   * ================================================================== */

  function ckCollectItems(submittingForm) {
    var stickyForms = document.querySelectorAll('form#bulk-add-to-cart');
    var sourceForms = stickyForms.length ? stickyForms : [submittingForm];

    var seen = {};
    var items = [];

    Array.prototype.forEach.call(sourceForms, function (form) {
      var inputs = form.querySelectorAll('input[name="id"]');
      Array.prototype.forEach.call(inputs, function (input) {
        var id = parseInt(input.value, 10);
        if (!id || seen[id]) return;
        seen[id] = true;
        var qty = parseInt(input.getAttribute('data-quantity'), 10) || 1;
        items.push({ id: id, quantity: qty });
      });
    });

    return items;
  }

  function ckSetBusy(busy) {
    var buttons = document.querySelectorAll(
      '.add_tocart_custom_button, #add_tocart_sticky_button'
    );
    Array.prototype.forEach.call(buttons, function (btn) {
      if (busy) {
        btn.setAttribute('aria-busy', 'true');
        btn.setAttribute('disabled', 'disabled');
      } else {
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('disabled');
      }
    });
  }

  function ckShowError(message) {
    window.alert(message || 'Sorry — this item could not be added to the cart.');
  }

  function ckOpenCart() {
    try {
      if (window.Rebuy && window.Rebuy.SmartCart) {
        if (typeof window.Rebuy.SmartCart.refresh === 'function') {
          window.Rebuy.SmartCart.refresh();
        }
        if (typeof window.Rebuy.SmartCart.show === 'function') {
          window.Rebuy.SmartCart.show();
          return;
        }
      }
    } catch (err) {
      console.warn('[' + CK_NS + '] Rebuy refresh failed, falling back:', err);
    }
    window.location.href = '/cart';
  }

  function ckAddToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (response.ok) return response.json();
      return response.json().then(function (data) {
        var msg =
          (data && (data.description || data.message)) ||
          'One of these items is currently unavailable.';
        throw new Error(msg);
      });
    });
  }

  function ckHandleSubmit(event) {
    event.preventDefault();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    var form = event.currentTarget;
    var items = ckCollectItems(form);

    if (!items.length) {
      console.error('[' + CK_NS + '] No variant IDs found in buy-box forms.');
      ckShowError('Please select a product option before adding to cart.');
      return;
    }

    ckSetBusy(true);
    console.log('[' + CK_NS + '] Adding to cart:', JSON.stringify(items));

    ckAddToCart(items)
      .then(function () {
        ckSetBusy(false);
        ckOpenCart();
      })
      .catch(function (err) {
        ckSetBusy(false);
        console.error('[' + CK_NS + '] Add to cart failed:', err);
        ckShowError(err && err.message);
      });
  }

  function ckBindForms() {
    var forms = document.querySelectorAll(
      'form.bulk-add-to-cart_button, form#bulk-add-to-cart'
    );
    Array.prototype.forEach.call(forms, function (form) {
      if (form.getAttribute('data-' + CK_NS + '-bound') === '1') return;
      form.setAttribute('data-' + CK_NS + '-bound', '1');
      form.addEventListener('submit', ckHandleSubmit);
    });
  }

  /* ================================================================== *
   * (3) DECORATION — includes sublines + flags (EDIT CONTENT HERE)
   * ================================================================== */

  // Card content map. Matched against the card title (h3), first hit wins.
  // Marketing-editable: change text here, redeploy the asset.
  var CK_CARD_META = [
    {
      match: /kitchen sink/i,
      flag: 'Complete Kit',
      flagClass: 'ck-flag--value',
      includes: 'The complete kit \u2014 14 pieces'
    },
    {
      match: /gummy edibles making kit/i,
      flag: 'Best Seller',
      flagClass: 'ck-flag--seller',
      includes: 'Everything to make gummies'
    },
    {
      match: /essentials/i,
      includes: 'Machine + core accessories'
    },
    {
      match: /.*/, // fallback: the bare machine card
      includes: 'Just the infuser'
    }
  ];

  function ckDecorateCards() {
    var cards = document.querySelectorAll('[data-addons-product]');
    Array.prototype.forEach.call(cards, function (card) {
      if (card.getAttribute('data-ck-decorated') === '1') return;
      var titleEl = card.querySelector('.v2-option_bottom h3');
      if (!titleEl) return;
      card.setAttribute('data-ck-decorated', '1');

      var title = titleEl.textContent || '';
      var meta = null;
      for (var i = 0; i < CK_CARD_META.length; i++) {
        if (CK_CARD_META[i].match.test(title)) { meta = CK_CARD_META[i]; break; }
      }
      if (!meta) return;

      if (meta.includes && !card.querySelector('.ck-included')) {
        var p = document.createElement('p');
        p.className = 'ck-included';
        p.textContent = meta.includes;
        titleEl.insertAdjacentElement('afterend', p);
      }

      // Build the inline expand ("what's in the box") from the card's old
      // tooltip modal content — shown when the card is selected (v1.3 layout).
      if (!card.querySelector('.ck-expand')) {
        var infoLink = card.querySelector('[data-button-handle-tooltip]');
        var modalId = infoLink && infoLink.getAttribute('data-tooltip-target');
        var modal = modalId ? document.getElementById(modalId) : null;
        var sourceList = modal ? modal.querySelector('.v2-modal_content ul') : null;

        if (sourceList) {
          card.setAttribute('data-ck-modal', modalId);
          var MAX_ITEMS = 6;
          var srcItems = sourceList.querySelectorAll('li');
          var expand = document.createElement('div');
          expand.className = 'ck-expand';
          var list = document.createElement('ul');

          var shown = Math.min(srcItems.length, MAX_ITEMS);
          for (var k = 0; k < shown; k++) {
            var li = document.createElement('li');
            li.textContent = (srcItems[k].textContent || '').replace(/\s+/g, ' ').trim();
            list.appendChild(li);
          }
          if (srcItems.length > MAX_ITEMS) {
            var more = document.createElement('li');
            more.className = 'ck-expand_more';
            more.textContent = '+ ' + (srcItems.length - MAX_ITEMS) + ' more included';
            list.appendChild(more);
          }
          expand.appendChild(list);
          card.appendChild(expand);
        }
      }

      if (meta.flag && !card.querySelector('.ck-flag')) {
        var f = document.createElement('span');
        f.className = 'ck-flag ' + (meta.flagClass || '');
        f.textContent = meta.flag;
        card.appendChild(f);
        card.classList.add('ck-has-flag');
        if (meta.flagClass) {
          card.classList.add(meta.flagClass.replace('ck-flag--', 'ck-tier-'));
        }
      }
    });

    // Hide the theme's own "Best Value" tab (renders inconsistently /
    // flickers on mobile). Scoped: only inside sections that contain cards.
    var candidates = document.querySelectorAll('div,span,p');
    Array.prototype.forEach.call(candidates, function (el) {
      if (el.classList.contains('ck-flag')) return;
      if (el.getAttribute('data-ck-hidden') === '1') return;
      if (el.children.length > 0) return;
      var txt = (el.textContent || '').trim();
      if (txt !== 'Best Value' && txt !== 'Complete Kit') return;
      var scope = el.closest('section, .shopify-section, [id^="shopify-section"]');
      if (!scope || !scope.querySelector('[data-addons-product]')) return;
      el.setAttribute('data-ck-hidden', '1');
      el.style.display = 'none';
    });
  }

  /* ================================================================== *
   * (3b) DESKTOP DETAIL PANEL — the selected card's full contents render
   * in one shared panel below the card grid (cards keep uniform height).
   * ================================================================== */

  function ckSyncExpandPanel() {
    var active = document.querySelector('[data-addons-product].active');
    var grid = active
      ? (active.closest('.v2-addons_options') || active.parentElement)
      : document.querySelector('.v2-addons_options');
    if (!grid) return;

    var panel = grid.parentElement.querySelector(':scope > .ck-expand-panel');
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'ck-expand-panel';
      panel.hidden = true;
      grid.insertAdjacentElement('afterend', panel);
    }

    var modalId = active && active.getAttribute('data-ck-modal');
    var modal = modalId ? document.getElementById(modalId) : null;
    var sourceList = modal ? modal.querySelector('.v2-modal_content ul') : null;

    if (!active || !sourceList) { panel.hidden = true; return; }

    var titleEl = active.querySelector('.v2-option_bottom h3');
    var title = titleEl ? (titleEl.textContent || '').replace(/\s+/g, ' ').trim() : '';

    panel.className = 'ck-expand-panel' +
      (active.classList.contains('ck-tier-seller') ? ' ck-expand-panel--seller' : '') +
      (active.classList.contains('ck-tier-value') ? ' ck-expand-panel--value' : '');

    var html = '<p class="ck-expand-panel_title">What\u2019s in the ' +
               title.replace(/</g, '&lt;') + '</p><ul>';
    var items = sourceList.querySelectorAll('li');
    for (var i = 0; i < items.length; i++) {
      html += '<li>' + (items[i].textContent || '').replace(/\s+/g, ' ').trim()
                .replace(/</g, '&lt;') + '</li>';
    }
    html += '</ul>';
    panel.innerHTML = html;
    panel.hidden = false;
  }

  /* ================================================================== *
   * (4) INFO MODAL — delegated open/close so the (i) works everywhere
   * ================================================================== */

  function ckHandleTooltipClick(event) {
    var opener = event.target.closest('[data-button-handle-tooltip]');
    var closer = event.target.closest('[data-modal-dismiss]');
    var overlay = event.target.classList &&
                  event.target.classList.contains('v2-modal_moreinformation')
                  ? event.target : null;

    if (!opener && !closer && !overlay) return;

    event.preventDefault();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation(); // never toggle twice via legacy handler
    }

    if (opener) {
      var id = opener.getAttribute('data-tooltip-target');
      var modal = id ? document.getElementById(id) : null;
      if (modal) {
        modal.classList.add('show');
        document.body.classList.add('ck-modal-open');
      }
      return;
    }

    var open = closer ? closer.closest('.v2-modal_moreinformation') : overlay;
    if (open) {
      open.classList.remove('show');
      document.body.classList.remove('ck-modal-open');
    }
  }

  document.addEventListener('click', ckHandleTooltipClick, true);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = document.querySelector('.v2-modal_moreinformation.show');
    if (open) {
      open.classList.remove('show');
      document.body.classList.remove('ck-modal-open');
    }
  });

  ckBindForms();
  ckCaptureBaseSelection();
  ckRecalcPriceLabel();
  ckDecorateCards();
  ckSyncExpandPanel();

  var ckObserver = new MutationObserver(function () {
    ckBindForms();
    ckDecorateCards();
  });
  ckObserver.observe(document.body, { childList: true, subtree: true });
})();